import { useState } from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  useTranslate,
  useCreate,
  useNotify
} from "react-admin";
import { useNavigate } from "react-router-dom";
import * as User from "providers/user";
import { usePhotoUploadSubmitCreate } from "hooks/usePhotoUploadSubmitCreate";
import CustomHeaderActions from "./CustomHeaderActions";
import { DefaultAvatarIcon } from "assets/Icons";
import * as Axios from "providers/axios";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";
import CustomImageInputSmall from "./CustomImageInputSmall";
import PhoneInput from "./PhoneInput";
import CustomPronounSelect from "./CustomPronounSelect";
import { CustomToolbar } from "./CustomToolbar";
import CustomDayAndMonthPicker from "./CustomDayAndMonthPicker";
import { correctDate } from "utils/correctDate";
import { isPhoneValid } from "utils/isPhoneValid";
import { parseGraphQLError } from "../utils/validation";

export default function ArtistCreate() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { permissions } = User.useUser();
  const navigate = useNavigate();
  const { submit: submitArtist } = usePhotoUploadSubmitCreate({
    redirect: `/dashboard/Artist`,
    resource: "Artist",
    keys: ["cover_photo_url"]
  });
  const { submit } = usePhotoUploadSubmitCreate({
    redirect: `/dashboard/Artist`,
    resource: "User",
    keys: ["cover_photo_url"]
  });
  const [create] = useCreate();
  const { api } = Axios.useAxios();
  const translate = useTranslate();
  const notify = useNotify();

  const handleFormSubmit = async (formData: any) => {
    setIsLoading(true);

    const handleError = (error: any, location: string) => {
      setIsLoading(false);
      const errorMessage = parseGraphQLError(error);
      notify(errorMessage, { type: "error" });
    };

    try {
      const payload = {
        ...formData,
        joined_company: { id: permissions.companyId }
      };

      if (formData.birthday) {
        payload.birthday = correctDate(formData.birthday);
      }

      const payloadArtist = {
        bio: formData.bio
      };

      submit(
        payload,
        async (userData: any, finalImage: string) => {
          try {
            create("CompanyUserDescription", {
              data: {
                description: formData.description,
                user: { id: userData.id },
                company: { id: permissions.companyId }
              }
            });

            await submitArtist(
              {
                ...payloadArtist,
                user: { id: userData.id },
                cover_photo_url: finalImage
              },
              () => {
                setIsLoading(false);
                navigate(`/dashboard/Artist`);
              },
              (artistError: any) => {
                handleError(artistError, "artist creation");
              }
            );

            await api.post(
              `${process.env.REACT_APP_API_CORE_URL}/v1/users/send-mail`,
              {
                id: userData.id,
                company_id: permissions.companyId,
                createArtist: true,
                createUser: true,
                cover_photo_url: finalImage
              }
            );

            setIsLoading(false);
          } catch (callbackError) {
            handleError(callbackError, "callback");
          }
        },
        (error: any) => {
          handleError(error, "user creation");
        }
      );
    } catch (error) {
      handleError(error, "outer");
    }
  };

  return (
    <Create
      actions={<CustomHeaderActions title={translate("pagenames.artist")} />}
    >
      <SimpleForm
        onSubmit={handleFormSubmit}
        resolver={yupResolver(validationSchema)}
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <TextInput
          fullWidth
          multiline
          source="bio"
          label={translate("artists.bio")}
        />
        <TextInput
          source="first_name"
          label={translate("users.first_name")}
          fullWidth
        />
        <TextInput
          source="last_name"
          label={translate("users.last_name")}
          fullWidth
        />
        <TextInput source="email" fullWidth />
        <PhoneInput source="phone_number" fullWidth checkSubmitted />
        <CustomDayAndMonthPicker label={translate("users.birthdate")} />
        <CustomPronounSelect
          source="pronoun"
          label="Pronoun"
          className="w-full"
        />
        <TextInput
          source="description"
          label={translate("users.description")}
          fullWidth
          sx={{ marginTop: "22px !important" }}
        />
        <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
          {translate("artists.artist_profile_photo")}:
        </div>
        <CustomImageInputSmall
          source="cover_photo_url"
          borderRadius="20px"
          buttonText={translate("common.set_photo")}
          bg="#B0ACA6"
          placeholder={<DefaultAvatarIcon />}
        />
      </SimpleForm>
    </Create>
  );
}

const validationSchema = yup
  .object()
  .shape({
    first_name: yup.string().required("First name required."),
    last_name: yup.string().required("Last name required."),
    email: yup
      .string()
      .email("Incorrectly formatted email.")
      .required("Email required."),
    photo_url: yup.object().nullable(),
    phone_number: yup
      .string()
      .test("phone", "Invalid phone number", (value) => {
        return isPhoneValid(value);
      }),
    birthday: yup.date().nullable(),
    pronoun: yup.string()
  })
  .required();
